import { User } from '@/interfaces/ranking.interface'
import LogServiceMock from '@/services/log/log.service.mock'
import localForageHelper from '@helpers/localforage.helper'
import { SocialNetworksAvailables } from '@interfaces/socialnetworks.interface'
import onEnvironmentChange from '@observables/environment.observable'
import onSocialNetworkConnection from '@observables/social-network-connection.observable'
import onUserChange from '@observables/user.observable'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import Loader from '../loader/loader.component'
import { SocialMediaIcon } from '../social-connect/social-connect.component'
import CircleButton from './circle-button/circle-button.component'
import TokenRenewButton from './token-renew-button/token-renew-button.component'

import './social-item.component.scoped.scss'

interface Props {
  item: SocialMediaIcon
}

export default ({ item }: Props) => {
  const [user, setUser] = useState<User | null | any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authUrl, setAuthUrl] = useState('')
  const logService = useMemo(() => new LogServiceMock(), [])
  logService.init('Component SocialItem')

  const { t } = useTranslation('socialItem')

  const title = useMemo(
    () => (item?.isEmpty ? t('toConnect') : `${item?.socialNetwork !== 'youtube' ? '@' : ''}${item?.username}`),
    [item, t]
  )

  const socialInfos = useMemo(() => {
    try {
      let profile
      if (item.socialNetwork === 'facebook') {
        profile = user?.instagram?.find(({ username }: any) => username === item?.username) || null
      } else {
        profile = user?.[item?.socialNetwork]?.find(({ username }: any) => username === item?.username) || null
      }
      const identity = user?.identities?.find((identity: any) => identity?.id === item?.id && identity?.connection === item?.socialNetwork)
      let provider = ''
      if (item?.socialNetwork === 'youtube') {
        provider = 'google-oauth2'
      } else if (item?.socialNetwork === 'facebook') {
        provider = 'instagram'
      } else {
        provider = item?.socialNetwork
      }
      const isTokenValid = !item?.isEmpty ? !user?.statusProviders?.[provider]?.find(({ id }: any) => id === item?.id)?.tokenExpired : true
      const isMacro = profile?.macro > 0

      return {
        profile,
        identity,
        isTokenValid,
        isMacro,
      }
    } catch (err) {
      logService?.error({
        message: `Error SocialItem: ${err}`,
        error: err,
      })
    }
  }, [item, user, logService])

  const onRenewToken = useCallback(
    async (idProfile: string, socialNetwork: string) => {
      await localForageHelper.setItem('actions', { action: 'renew', idProfile })
      window.location.href = `${authUrl}/${socialNetwork === 'instagram' ? 'facebook' : socialNetwork}?squid-callback=${
        window.location.href
      }`
    },
    [authUrl]
  )

  const handleRenewToken = useCallback(
    async (idProfile?: string, socialNetwork?: string) => {
      await localForageHelper.setItem('actions', { action: 'renew', idProfile })
      if (['instagram', 'facebook'].includes(socialNetwork)) {
        onSocialNetworkConnection.next({
          socialNetwork: socialNetwork as SocialNetworksAvailables,
          connect: true,
        })
      } else {
        await onRenewToken(idProfile, socialNetwork)
      }
    },
    [onRenewToken]
  )

  const handleClick = useCallback(async () => {
    if (item?.isEmpty) {
      await localForageHelper.setItem('actions', { action: 'addAccount' })
      onSocialNetworkConnection.next({
        socialNetwork: item?.socialNetwork as SocialNetworksAvailables,
        connect: true,
      })
    } else if (!socialInfos?.isTokenValid) {
      await handleRenewToken(item?.id, item?.socialNetwork)
    } else {
      history.pushState({}, '', '/profile/social-networks')
    }
  }, [handleRenewToken, item?.id, item?.isEmpty, item?.socialNetwork, socialInfos?.isTokenValid])

  useEffect(() => {
    const onUserChangeSubscription: SubscriptionLike = onUserChange.subscribe((userChanged) => {
      setUser(userChanged)
      setIsLoading(false)
    })
    return () => onUserChangeSubscription?.unsubscribe()
  }, [])

  useEffect(() => {
    const onEnvironmentChangeSubscription: SubscriptionLike = onEnvironmentChange.subscribe((environment) => {
      setAuthUrl(environment?.squidApis?.proxy)
    })

    return () => onEnvironmentChangeSubscription?.unsubscribe()
  }, [])

  if (isLoading) {
    return (
      <div className="text-center py-3" style={{ width: '100%' }}>
        <Loader size="small" />
      </div>
    )
  }

  return (
    <div
      className="item box-shadow"
      key={item?.socialNetwork}
      style={{
        backgroundColor: item?.bgColor,
        cursor: 'pointer',
      }}
      role="button"
      tabIndex={0}
      onClick={async () => await handleClick()}
    >
      {item?.icon}
      {!socialInfos?.isTokenValid && !socialInfos?.isMacro && <TokenRenewButton />}
      <CircleButton socialNetwork={item?.socialNetwork} isEmpty={item?.isEmpty} />
      <div className="label">
        <span className="label-content" title={title}>
          {title}
        </span>
      </div>
    </div>
  )
}
